import {
  buildTranscriptLines,
  formatTranscriptDelta,
  type TranscriptDisplayChunk,
} from './meetings-transcript.utils';

import type { TranscriptDeltaPayload } from './meetings.types';

export type LiveTranscriptChunk = TranscriptDisplayChunk & {
  chunkId: number;
  isFinal?: true;
};

export type LiveTranscriptState = {
  transcript: string;
  interimChunks: LiveTranscriptChunk[];
  currentSpeaker: string | undefined;
};

export type LiveTranscriptTraceEvent = {
  type: 'ui_live_transcript_delta';
  source: string;
  delta: TranscriptDeltaPayload;
  before: LiveTranscriptTraceState;
  after: LiveTranscriptTraceState;
  textToAppend?: string;
};

type LiveTranscriptTraceState = LiveTranscriptState & {
  lines: string[];
};

export type LiveTranscriptTraceSink = (event: LiveTranscriptTraceEvent) => void;

type AppendLiveTranscriptOptions = {
  trace?: LiveTranscriptTraceSink;
  traceSource?: string;
};

export function appendLiveTranscriptDelta(
  state: LiveTranscriptState,
  delta: TranscriptDeltaPayload,
  options: AppendLiveTranscriptOptions = {}
): LiveTranscriptState {
  const supersededChunkIds = new Set(delta.supersededChunkIds);
  let nextState: LiveTranscriptState;
  let textToAppend: string | undefined;

  if (delta.isFinal) {
    const finalResult = appendFinalMultiChunkDelta(
      state,
      delta,
      supersededChunkIds
    );

    nextState = finalResult.state;
    textToAppend = finalResult.textToAppend;
  } else {
    const interimChunk = chunkFromDelta(delta);
    const interimChunks = removeSupersededChunks(
      state.interimChunks,
      supersededChunkIds
    );

    nextState = {
      ...state,
      interimChunks: upsertInterimChunk(interimChunks, interimChunk),
    };
  }

  traceLiveTranscriptDelta({
    trace: options.trace,
    source: options.traceSource,
    delta,
    before: state,
    after: nextState,
    textToAppend,
  });
  return nextState;
}

function appendFinalMultiChunkDelta(
  state: LiveTranscriptState,
  delta: TranscriptDeltaPayload,
  supersededChunkIds: Set<number>
): { state: LiveTranscriptState; textToAppend: string } {
  const replacementIndex = findFinalReplacementIndex(
    state.interimChunks,
    delta
  );

  if (replacementIndex === -1) {
    const textToAppend = formatTranscriptDelta(
      state.transcript,
      state.currentSpeaker,
      delta
    );
    return {
      state: {
        transcript: state.transcript + textToAppend,
        interimChunks: removeSupersededChunks(
          state.interimChunks,
          supersededChunkIds
        ),
        currentSpeaker: delta.speaker,
      },
      textToAppend,
    };
  }

  const removalIndexes = getFinalRemovalIndexes(
    state.interimChunks,
    replacementIndex,
    supersededChunkIds
  );
  const remainingChunks = state.interimChunks.filter(
    (_chunk, index) => !removalIndexes.has(index)
  );
  const insertionIndex = state.interimChunks
    .slice(0, replacementIndex)
    .filter((_chunk, index) => !removalIndexes.has(index)).length;
  const chunksWithFinal = [
    ...remainingChunks.slice(0, insertionIndex),
    { ...chunkFromDelta(delta), isFinal: true as const },
    ...remainingChunks.slice(insertionIndex),
  ];

  return flushLeadingFinalChunks(state, chunksWithFinal);
}

function findFinalReplacementIndex(
  chunks: LiveTranscriptChunk[],
  delta: TranscriptDeltaPayload
): number {
  return findLastChunkIndex(chunks, (chunk) => chunk.chunkId === delta.chunkId);
}

function flushLeadingFinalChunks(
  state: LiveTranscriptState,
  chunks: LiveTranscriptChunk[]
): { state: LiveTranscriptState; textToAppend: string } {
  let transcript = state.transcript;
  let currentSpeaker = state.currentSpeaker;
  let flushCount = 0;
  let textToAppend = '';

  for (const chunk of chunks) {
    if (!chunk.isFinal) break;

    const chunkTextToAppend = formatTranscriptDelta(
      transcript,
      currentSpeaker,
      chunk
    );
    transcript += chunkTextToAppend;
    currentSpeaker = chunk.speaker;
    textToAppend += chunkTextToAppend;
    flushCount += 1;
  }

  return {
    state: {
      transcript,
      interimChunks: chunks.slice(flushCount),
      currentSpeaker,
    },
    textToAppend,
  };
}

function getFinalRemovalIndexes(
  chunks: LiveTranscriptChunk[],
  replacementIndex: number,
  supersededChunkIds: Set<number>
): Set<number> {
  const removalIndexes = new Set<number>([replacementIndex]);

  if (supersededChunkIds.size > 0) {
    chunks.forEach((chunk, index) => {
      if (supersededChunkIds.has(chunk.chunkId)) {
        removalIndexes.add(index);
      }
    });
  }

  return removalIndexes;
}

function removeSupersededChunks(
  chunks: LiveTranscriptChunk[],
  supersededChunkIds: Set<number>
): LiveTranscriptChunk[] {
  if (supersededChunkIds.size === 0) return chunks;

  return chunks.filter((chunk) => !supersededChunkIds.has(chunk.chunkId));
}

function findLastChunkIndex(
  chunks: LiveTranscriptChunk[],
  predicate: (chunk: LiveTranscriptChunk) => boolean
): number {
  for (let index = chunks.length - 1; index >= 0; index -= 1) {
    if (predicate(chunks[index])) return index;
  }

  return -1;
}

function upsertInterimChunk(
  chunks: LiveTranscriptChunk[],
  nextChunk: LiveTranscriptChunk
): LiveTranscriptChunk[] {
  const existingIndex = findOpenInterimChunkIndex(chunks, nextChunk);

  if (existingIndex !== -1) {
    return chunks.map((chunk, index) =>
      index === existingIndex ? nextChunk : chunk
    );
  }

  // The same chunk id is reopening as a new turn (the other lane spoke in
  // between). Seal the prior, now-closed interim for this id as final so it can
  // flush to the committed transcript instead of permanently blocking later
  // finals queued behind it.
  return [...sealClosedInterims(chunks, nextChunk.chunkId), nextChunk];
}

function sealClosedInterims(
  chunks: LiveTranscriptChunk[],
  chunkId: number
): LiveTranscriptChunk[] {
  return chunks.map((chunk) =>
    chunk.chunkId === chunkId && !chunk.isFinal
      ? { ...chunk, isFinal: true as const }
      : chunk
  );
}

function findOpenInterimChunkIndex(
  chunks: LiveTranscriptChunk[],
  nextChunk: LiveTranscriptChunk
): number {
  for (let index = chunks.length - 1; index >= 0; index -= 1) {
    const chunk = chunks[index];
    if (chunk.chunkId !== nextChunk.chunkId) continue;
    if (chunk.isFinal) return -1;

    const turnClosed = chunks
      .slice(index + 1)
      .some((laterChunk) => laterChunk.speaker !== nextChunk.speaker);
    return turnClosed ? -1 : index;
  }

  return -1;
}

function chunkFromDelta(delta: TranscriptDeltaPayload): LiveTranscriptChunk {
  return {
    chunkId: delta.chunkId,
    speaker: delta.speaker,
    text: delta.text,
  };
}

function traceLiveTranscriptDelta({
  trace,
  source,
  delta,
  before,
  after,
  textToAppend,
}: {
  trace: LiveTranscriptTraceSink | undefined;
  source: string | undefined;
  delta: TranscriptDeltaPayload;
  before: LiveTranscriptState;
  after: LiveTranscriptState;
  textToAppend?: string;
}): void {
  if (!trace) return;

  const event: LiveTranscriptTraceEvent = {
    type: 'ui_live_transcript_delta',
    source: source ?? 'unknown',
    delta,
    before: {
      transcript: before.transcript,
      currentSpeaker: before.currentSpeaker,
      interimChunks: before.interimChunks,
      lines: buildTranscriptLines(before.transcript, before.interimChunks),
    },
    after: {
      transcript: after.transcript,
      currentSpeaker: after.currentSpeaker,
      interimChunks: after.interimChunks,
      lines: buildTranscriptLines(after.transcript, after.interimChunks),
    },
  };
  if (textToAppend !== undefined) {
    event.textToAppend = textToAppend;
  }
  trace(event);
}
